import { Flex } from 'antd';
import styles from './transcript-message.module.scss';
import User from '@/assets/icons/user-01.svg?react';
import Target from '@/assets/icons/target-04.svg?react';
import Documents from '@/assets/icons/file-06.svg?react';
import Star from '@/assets/icons/star-04.svg?react';
import Icon from '@ant-design/icons';
import { calculateHourFromISODate, formatToHTML } from '@/utils/helpers';
import clsx from 'clsx';
import TopicChip from '../topic-chip/topic-chip';
import { Link } from 'react-router-dom';

interface AnswerMessage {
  type: 'answer';
  detectedKeywords: string[];
  document_id?: number;
}

interface QuestionMessage {
  type: 'question';
}

type TranscriptMessageProps = (AnswerMessage | QuestionMessage) & {
  messageText: string;
  date: string;
  isAgentCall?: boolean;
};

export function TranscriptMessage({
  type,
  messageText,
  date,
  isAgentCall,
  ...props
}: TranscriptMessageProps) {
  const keywords =
    'detectedKeywords' in props && props.detectedKeywords.length
      ? props.detectedKeywords
      : undefined;

  const documentId = 'document_id' in props && props.document_id ? props.document_id : undefined;

  const renderIcon = isAgentCall ? (
    type === 'question' ? (
      <User data-testid="user-icon" stroke={styles.userColorIcon} width={16} height={16} />
    ) : (
      <User data-testid="user-icon" stroke={styles.starColorIcon} width={16} height={16} />
    )
  ) : type === 'answer' ? (
    <User data-testid="user-icon" stroke={styles.userColorIcon} width={16} height={16} />
  ) : (
    <Star
      data-testid="star-icon"
      fill={styles.starColorIcon}
      stroke={styles.starColorIcon}
      width={16}
      height={16}
    />
  );

  return (
    <Flex vertical className={clsx(styles.wrapper)}>
      <Flex
        gap={15}
        className={clsx(type === 'question' ? styles.questionContainer : styles.answerContainer)}
      >
        <Flex vertical>
          <Flex
            vertical
            gap={8}
            className={clsx(
              styles.borderedContainer,
              isAgentCall
                ? type === 'question'
                  ? styles.borderedAnswerContainerAgent
                  : styles.borderedQuestionContainerAgent
                : type === 'answer'
                  ? styles.borderedQuestionContainer
                  : styles.borderedAnswerContainer,
              keywords && styles.borderedContainerWithKeyword
            )}
          >
            <div
              className={styles.message}
              dangerouslySetInnerHTML={{ __html: formatToHTML(messageText) }}
            />

            {keywords && (
              <Flex
                justify="center"
                align="center"
                gap={4}
                className={styles.keywordDetectedContainer}
              >
                <Icon
                  component={() => (
                    <Target stroke={styles.targetColorIcon} width={16} height={16} />
                  )}
                />
                <p className={styles.keywordHeading}>Keyword Detected: </p>
                {[...new Set(keywords)].map((element) => (
                  <TopicChip className={styles.keywordChip} title={element} key={element} />
                ))}
              </Flex>
            )}

            {documentId !== undefined && (
              <Flex justify="right" align="center" gap={4} className={styles.documentIdContainer}>
                <Icon
                  component={() => (
                    <Documents stroke={styles.targetColorIcon} width={16} height={16} />
                  )}
                />
                <p className={styles.documentIdHeading}>Document: </p>

                <Link to={`/documents/${documentId}`}>{documentId}</Link>
              </Flex>
            )}
          </Flex>
        </Flex>

        <Icon
          className={clsx(
            styles.icon,
            isAgentCall
              ? type === 'question'
                ? styles.starIcon
                : styles.userIcon
              : type === 'question'
                ? styles.userIcon
                : styles.starIcon
          )}
          component={() => renderIcon}
        />
      </Flex>
      <p className={clsx(styles.hour, type === 'answer' ? styles.questionHour : styles.answerHour)}>
        {calculateHourFromISODate(date)}
      </p>
    </Flex>
  );
}
